import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PictureInPicture from 'containers/Player/components/PictureInPicture';
import HeadBand from 'containers/Headband';
import LoadingSpinner from 'components/forms/LoadingSpinner';
import ModalFactory from 'components/modal/ModalFactory';
import _ from 'lodash';
import { isOnApaKeyForUserSuspended } from 'utils/isOnApaKeyForUserSuspended';
import UserSuspendedModal from 'containers/UserSuspendedModal';
import { converter } from 'helpers/converter';
import { Footer } from '../../../containers/Footer';
import * as actions from '../../../actions/response';
import { BAD_RESPONSE_ERROR_MSG, GetRegion } from '../../../constants';
import { isNodeMyContent } from 'utils/Utils';
import Menu from 'containers/Menu/Menu';
import useMenuRedirect from 'containers/Menu/hooks/useMenuRedirect';
import { useProfiles } from '../../../containers/ProfileManager/hooks/useProfiles';
import { ProfileManager } from '../../../containers/ProfileManager';
import { useLoginFromMobile } from 'hooks/useLoginFromMobile';
import ContentLoader from 'containers/Player/components/ContentLoader';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import OldFooter from 'components/footer/Footer';

const PATHS_WITHOUT_HEADER_FOOTER = [
  '/completeinfo',
  '/terms',
  '/search',
  '/talent',
  '/userdetectwsregister',
  '/userdetectwsconfirm',
  '/iptelmexactivate',
  '/login_redirect',
  '/oauth_callback_login',
];

const verifyLocations = (pathname, paths) =>
  paths.find(path => pathname.includes(path));

export const useNodesLayoutData = ({ location, params }) => {
  const [searchParams] = useSearchParams();
  const translations = useSelector(state => state.translations);
  const showModal = useSelector(state => state.profiles?.modal?.show);
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();
  const path = window.location.pathname !== '/';
  const pictureInPicture = useSelector(state => state.Player.PictureInPicture);
  const modal = useSelector(state => state.modal);
  const [isOnUserSuspended, setIsOnUserSuspended] = useState(false);
  const { genericError, genericMessage } = useSelector(state => state.modal);
  const { navData } = useSelector(state => state?.cvNavData);
  const showProfileLoader = useSelector(
    state => state?.profilesManager?.showLoader,
  );
  const epgTransactions = useSelector(
    state => state.Player.EpgTransactions.epgTransactions,
  );
  const { code: codeMenu } = navData?.selected || {};
  const isMycontentNode = isNodeMyContent(codeMenu);
  const isSetControlpin = location.pathname?.includes('setcontrolpin');
  const claroLiteMetadata = useSelector(
    selectMetadataKeyByRegion('cv_advertising_config'),
  );

  const { firstNode, nodes } = useMenuRedirect();
  const { profilesEnable } = useProfiles();
  const showHeadBand =
    location.pathname.indexOf(`/${GetRegion()}/${firstNode}`) > -1 &&
    !epgTransactions.isOpen;

  const hideHeaderAndFooter = verifyLocations(
    location?.pathname,
    PATHS_WITHOUT_HEADER_FOOTER,
  );

  const noShowHF =
    searchParams.get('device_model') === 'aapl' ||
    searchParams.get('device_model') === 'android';
  const { isMyAccountFromMobile } = useLoginFromMobile();
    const { modalVisibility } = useSelector(state => state.resetPassword);
    const isForgettenPassword = window.location.pathname?.includes('forgottenPassword');

  useEffect(
    () => {
      if (user.acceptTerms === 1) {
        setIsOnUserSuspended(isOnApaKeyForUserSuspended());
      }
      validTermsAndConditions();
    },
    [user],
  );

  useEffect(
    () => {
      if (!showModal) document.body.classList.remove('no-body-scroll');
    },
    [showModal],
  );

  useEffect(
    () => {
      if (
        genericError &&
        translations &&
        Object.keys(translations.bad_response_error_msg)
      ) {
        const errorModal = {
          modalType: 'ErrorSimple',
          props: {
            message:
              genericMessage ||
              converter.convertApa(translations, BAD_RESPONSE_ERROR_MSG),

            onAccept: () => {
              dispatch(actions.removeModal('ErrorSimple'));
              dispatch(actions.errorNotControlledClear());
            },
            onClose: () => {
              dispatch(actions.removeModal('ErrorSimple'));
              dispatch(actions.errorNotControlledClear());
            },
          },
        };
        dispatch(actions.showModal(errorModal));
      }
    },
    [genericError],
  );

  const renderPictureInPicture = () =>
    pictureInPicture.showing && <PictureInPicture />;

  const renderHeadBand = () => showHeadBand && <HeadBand />;

  const renderLoadingSpinner = () => !path && <LoadingSpinner />;

  const renderModal = () => ModalFactory.create(modal);

  const validTermsAndConditions = () => {
    let terms = localStorage.getItem('loginInfo.needsAcceptTerms');
    let userWS = {};
    try {
      terms = JSON.parse(terms);
      userWS = _.get(
        terms,
        ['completeError', 'response', 'userDetectWS'],
        false,
      );
    } catch (e) {
      console.error(e);
      userWS = false;
    }

    if (userWS) {
      const m = {
        modalType: 'TermsAndConditions',
        props: {
          userDetectWS: userWS,
        },
      };
      dispatch(actions.showModal(m));
    }
  };

  const renderUserSuspendedModal = () =>
    isOnUserSuspended && <UserSuspendedModal />;

  // const renderProfilesContainer = () =>
  //   profilesEnable && path && <ProfilesContainer key="profile" />;

  const renderProfilesContainer = () =>
    profilesEnable &&
    path &&
    !isMyAccountFromMobile &&
    !isSetControlpin && !isForgettenPassword && !modalVisibility && <ProfileManager />;

  const showHeader =
    location?.pathname?.includes('landing') ||
    location?.pathname?.includes('terms') ||
    location?.pathname?.includes('login') ||
    location?.pathname?.includes('register') ||
    location?.pathname?.includes('talent') ||
    location?.pathname?.includes('completeinfo');
    


  const renderHeader = () =>
    !showHeader && !noShowHF && nodes?.length && <Menu params={params} location={location} />;

  const renderFooter = () => !noShowHF && !hideHeaderAndFooter && <Footer />;

  const renderOldFooter = footerRef => {
    const isAdsEnabled = claroLiteMetadata?.enable;
    const isVisible = Boolean(!noShowHF && !hideHeaderAndFooter);

    return (
      <OldFooter
        isVisible={isVisible}
        isAdsEnabled={isAdsEnabled}
        ref={footerRef}
      />
    );
  };

  const isInSearcher = window.location.pathname?.includes('search');
  const renderLoader = showProfileLoader && <ContentLoader />;

  return {
    renderPictureInPicture,
    renderHeadBand,
    renderLoadingSpinner,
    renderModal,
    validTermsAndConditions,
    renderUserSuspendedModal,
    renderProfilesContainer,
    renderHeader,
    renderFooter,
    renderOldFooter,
    isInSearcher,
    isMycontentNode,
    renderLoader,
    showHeadBand,
  };
};

export default useNodesLayoutData;
